import { useCallback, useEffect, useLayoutEffect, useState, type JSX } from 'react';
import { api } from '../api/client';
import type { TermDeckWorkbenchDto, WorkbenchClaudeModel, WorkbenchLaunchChoiceDto } from '../api/types';
import { useDeckStore } from '../store/deck-store';
import { navigate } from '../lib/router';
import { CURRENT_SONNET_MODEL, MODEL_LABELS, familyOf, isModelId } from '@term-deck/core/model.js';
import { CODEX_MODEL_LABELS, DEFAULT_CODEX_MODEL, isCodexModelId, PROJECT_CODEX_EFFORT } from '@term-deck/core/codex-model.js';

type Assistant = 'codex' | 'sonnet';

const ASSISTANTS: Array<{ id: Assistant; title: string; detail: string; provider: 'codex' | 'claude'; model?: WorkbenchClaudeModel; effort: 'medium' | 'high' | 'max' }> = [
  // 30/09/2026 — o Codex do produto abre no padrão do term-deck (Terra vigente/max); o rótulo vem do catálogo.
  { id: 'codex', title: CODEX_MODEL_LABELS[DEFAULT_CODEX_MODEL], detail: 'Codex · padrão do produto', provider: 'codex', effort: PROJECT_CODEX_EFFORT },
  // 31/10/2026 (ed3018fe): o Claude do Produto é o Sonnet vigente; Opus só com justificativa registrada (API/MCP).
  { id: 'sonnet', title: MODEL_LABELS[CURRENT_SONNET_MODEL], detail: 'Claude Code · alto', provider: 'claude', model: CURRENT_SONNET_MODEL, effort: 'high' },
];

function assistantOf(launch: TermDeckWorkbenchDto['launch']): Assistant {
  if (launch?.provider !== 'claude') return 'codex';
  // Preferências antigas (Opus/Fable) continuam observáveis, mas novas escolhas usam o Sonnet vigente.
  return 'sonnet';
}

export function TermDeckWorkbenchBadge({ expanded = false }: { expanded?: boolean }): JSX.Element {
  const setFocused = useDeckStore((value) => value.setFocused);
  const [state, setState] = useState<TermDeckWorkbenchDto | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [assistant, setAssistant] = useState<Assistant>('codex');
  const [effort, setEffort] = useState<'medium' | 'high' | 'max'>(PROJECT_CODEX_EFFORT);
  const sessions = useDeckStore(value => value.sessions);
  const [loaded, setLoaded] = useState(false);
  const refresh = useCallback(() => {
    void api
      .getTermDeckWorkbench({ view: 'metadata' })
      .then((value) => {
        setState(value);
        setError(value ? null : 'Terminal do produto indisponível.');
        setLoaded(true);
      })
      .catch(() => {
        setError('Não foi possível consultar o terminal do produto.');
        setLoaded(true);
      });
  }, []);
  useEffect(() => {
    refresh();
    const timer = window.setInterval(refresh, 30_000);
    return () => window.clearInterval(timer);
  }, [refresh]);
  // Keep the selector coherent with the accepted request before its controls become clickable.
  useLayoutEffect(() => {
    const launch = state?.pendingLaunch?.choice ?? state?.launch;
    if (launch) {
      const next = assistantOf(launch);
      setAssistant(next);
      const legacyFable = launch.provider === 'claude' && launch.model && isModelId(launch.model) && familyOf(launch.model) === 'fable';
      setEffort(legacyFable ? 'high' : launch.effort ?? ASSISTANTS.find((item) => item.id === next)!.effort);
    }
  }, [state?.launch?.provider, state?.launch?.effort, state?.launch?.model, state?.pendingLaunch?.id,
    state?.pendingLaunch?.choice.provider, state?.pendingLaunch?.choice.effort, state?.pendingLaunch?.choice.model]);
  const online = state?.binding.status === 'online';
  const choice = (): WorkbenchLaunchChoiceDto => {
    const selected = ASSISTANTS.find((item) => item.id === assistant)!;
    return { provider: selected.provider, effort, ...(selected.model ? { model: selected.model } : {}) };
  };
  const focus = async (id: string): Promise<void> => {
    await useDeckStore.getState().refreshSessions();
    setFocused(id);
    navigate('/deck?focus=' + encodeURIComponent(id));
  };
  const open = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const next = await api.openTermDeckWorkbench(choice());
      if (!next?.binding.sessionId)
        throw new Error('A abertura não retornou um terminal. Consulte novamente.');
      setState(next);
      await focus(next.binding.sessionId);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Não foi possível abrir.');
    } finally {
      setBusy(false);
    }
  };
  const reopen = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await api.reopenTermDeckWorkbench({ ...choice(), ...(state?.pendingLaunch ? { expectedPendingId: state.pendingLaunch.id } : {}) });
      setState(result.workbench);
      setNotice(
        result.scheduled
          ? 'Troca agendada. O assistente será trocado assim que o turno terminar e o terminal estiver livre.'
          : result.queued > 0
          ? `Assistente trocado. ${result.queued} pedido(s) na fila voltam para a sessão nova quando ela ficar ociosa.`
          : 'Assistente trocado. As alterações em andamento continuam no repositório.',
      );
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Não foi possível trocar o assistente.');
    } finally {
      setBusy(false);
    }
  };
  const cancel = async (): Promise<void> => {
    if (!state?.pendingLaunch) return;
    setBusy(true); setError(null); setNotice(null);
    try {
      const next = await api.cancelTermDeckWorkbenchLaunch(state.pendingLaunch.id);
      setState(next);
      setNotice(next?.binding.status === 'online' ? 'Troca cancelada. O assistente atual continua.' : 'Troca cancelada. O terminal permanece fechado.');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Não foi possível cancelar. Consulte novamente.');
      refresh();
    } finally { setBusy(false); }
  };
  if (!expanded)
    return (
      <button
        type="button"
        className={'termdeck-workbench-badge' + (online ? ' is-online' : '')}
        data-testid="termdeck-workbench-slot"
        onClick={() => navigate('/product')}
        title="Terminal do produto e escolha de assistente"
      >
        <span className="termdeck-workbench-mark" aria-hidden>
          TD
        </span>
        <span>produto</span>
        <span className="termdeck-workbench-state">{online ? 'online' : 'abrir'}</span>
      </button>
    );
  const picker = (
    <>
      <div className="workbench-models" role="group" aria-label="Assistente do produto">
        {ASSISTANTS.map((item) => (
          <button
            key={item.id}
            type="button"
            aria-pressed={assistant === item.id}
            onClick={() => {
              setAssistant(item.id);
              setEffort(item.effort);
            }}
          >
            <strong>{item.title}</strong>
            <span>{item.detail}</span>
          </button>
        ))}
      </div>
      <label className="workbench-effort">
        Esforço
        <select
          aria-label="Esforço do produto"
          value={effort}
          onChange={(e) => setEffort(e.target.value as 'medium' | 'high' | 'max')}
        >
          <option value="medium">Médio</option>
          <option value="high">Alto</option>
          {assistant === 'codex' && <option value="max">Máximo</option>}
        </select>
      </label>
    </>
  );
  const liveCodexModel = sessions?.find(session => session.id === state?.binding.sessionId)?.codexModel;
  const currentTitle = state?.launch?.provider === 'claude' && state.launch.model && isModelId(state.launch.model)
    ? MODEL_LABELS[state.launch.model]
    : isCodexModelId(liveCodexModel) ? CODEX_MODEL_LABELS[liveCodexModel] : 'Codex';
  return (
    <div className="workbench-launch-options" data-testid="termdeck-workbench-slot">
      {error && (
        <p role="alert">
          {error}{' '}
          <button type="button" onClick={refresh}>
            Tentar novamente
          </button>
        </p>
      )}
      {notice && <p role="status">{notice}</p>}
      {state?.pendingLaunch && (
        <div className="workbench-pending-launch" role="group" aria-label="Troca de assistente pendente">
          <p role="status">
            {state.pendingLaunch.phase === 'scheduled' ? 'Troca agendada' : 'Troca em andamento'}
            {' para '}{isCodexModelId(state.pendingLaunch.model) ? CODEX_MODEL_LABELS[state.pendingLaunch.model]
              : isModelId(state.pendingLaunch.model) ? MODEL_LABELS[state.pendingLaunch.model] : state.pendingLaunch.model}
            {' · esforço '}{state.pendingLaunch.choice.effort === 'max' ? 'máximo' : state.pendingLaunch.choice.effort === 'medium' ? 'médio' : 'alto'}.
            {state.pendingLaunch.phase === 'scheduled' ? ' Aguarda o fim do turno e o terminal livre.' : ' Acompanhe a confirmação da nova sessão.'}
          </p>
          <p className="product-help">Solicitada por {state.pendingLaunch.actorName} em {new Date(state.pendingLaunch.requestedAtMs).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })} (Brasília).</p>
          {state.pendingLaunch.lastError && <p role="alert">A troca precisa de conferência. A tentativa foi preservada; consulte o terminal antes de abrir outra sessão.</p>}
          <button className="product-primary-action" type="button" disabled={busy || state.pendingLaunch.phase !== 'scheduled'} onClick={() => void cancel()}>Cancelar troca</button>
        </div>
      )}
      {!loaded ? (
        <p role="status">Consultando terminal…</p>
      ) : online ? (
        <>
          <p className="workbench-online">
            <span aria-hidden>●</span> Seu terminal está aberto
            {currentTitle ? ` com ${currentTitle}` : ''}. O trabalho continua na sessão atual.
          </p>
          <button
            type="button"
            className="product-primary-action"
            disabled={busy}
            onClick={() => {
              if (state?.binding.sessionId) {
                setBusy(true);
                void focus(state.binding.sessionId)
                  .catch(() => setError('Não foi possível focar o terminal.'))
                  .finally(() => setBusy(false));
              }
            }}
          >
            Ir para o terminal
          </button>
          <details className="workbench-switch">
            <summary>Trocar assistente</summary>
            {picker}
            <button type="button" disabled={busy || !state || (!!state.pendingLaunch && state.pendingLaunch.phase !== 'scheduled')} onClick={() => void reopen()}>
              {busy ? 'Registrando…' : state?.pendingLaunch ? 'Alterar troca agendada' : 'Trocar quando estiver livre'}
            </button>
            <p className="product-help">
              A troca espera o terminal ficar ocioso. Pedidos na fila do produto e alterações no
              repositório continuam na sessão nova.
            </p>
          </details>
        </>
      ) : (
        <>
          {picker}
          <button
            type="button"
            className="product-primary-action"
            disabled={busy || !state || !!state.pendingLaunch}
            onClick={() => void open()}
          >
            {busy ? 'Abrindo…' : 'Abrir terminal do produto'}
          </button>
          <p className="product-help">{state?.pendingLaunch ? 'Acompanhe a troca pendente acima antes de abrir outro terminal.' : 'Sua escolha é lembrada na próxima abertura.'}</p>
        </>
      )}
    </div>
  );
}
